import React, { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { FormikProvider, useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import AfterMintItemModal from "../AfterMintItemModal";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { physicalItemSchema } from "validators/client/physicalItemOffchain";

import { selectUser } from "redux/slices/metamask";

import MintItemModal from "sections/itemsPage/MintItemModal";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import Button from "components/CustomButton";
import CustomOutlinedInput from "components/CustomOutlinedInput";

import useLogger from "hooks/useLogger";

import { STORAGE } from "enums/storage";
import { ACTION_TYPES } from "enums/logger/ActionTypes";
import { PATHS, PHYSICAL_TO_NFT_STEPS } from "enums/logger/Paths";
import { NFC_MINT_TYPES } from "enums/nfcMintTypes";

import { getObjectItem } from "utils/storage";

interface MintItemFormProps {
  data: {
    hash: string;
    contractAddress: string;
    nfcContractAddress?: string | null;
    tokenId: number;
    itemName: string;
  };
  isCards: boolean;
  canMint: boolean;
  disabled?: boolean;
  isPinCodeRequired?: boolean;
  nicknameLabel?: string;
  buttonText?: string;
}

const MintItemForm: FC<MintItemFormProps> = ({
  data,
  disabled = false,
  isCards,
  canMint,
  isPinCodeRequired = false,
  nicknameLabel,
  buttonText,
}) => {
  const user = useSelector(selectUser);

  const [isMintModalOpen, setMintModalOpen] = useState(false);
  const [isAfterMintModalOpen, setAfterMintModalOpen] = useState(false);
  const log = useLogger();
  const storageData = getObjectItem<{ nickname: string; email: string }>(
    STORAGE.FORM_DATA,
  );

  const handleSubmit = useCallback(async () => {
    if (!canMint) {
      return;
    }
    setMintModalOpen(true);
    log({
      path: PATHS.PHYSICAL_TO_NFT,
      step: PHYSICAL_TO_NFT_STEPS.OPEN_MODAL,
      data,
      actionType: ACTION_TYPES.USER,
    });
  }, [log, data, canMint]);

  const handleMintModalClose = useCallback(() => {
    setMintModalOpen(false);
    log({
      path: PATHS.PHYSICAL_TO_NFT,
      step: null,
      data,
      actionType: ACTION_TYPES.USER,
    });
  }, [log, data]);

  const handleMinted = useCallback(() => {
    handleMintModalClose();
    setAfterMintModalOpen(true);
  }, [handleMintModalClose]);

  const formik = useFormik({
    initialValues: {
      nickname: user?.nickname ?? storageData?.nickname ?? "",
      email: user?.email ?? storageData?.email ?? "",
      confirmEmail: user?.email ?? storageData?.email ?? "",
    },

    validationSchema: toFormikValidationSchema(physicalItemSchema),
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <Box>
      <FormikProvider value={formik}>
        {/* If user has all required variables, display a quick mint form */}
        {user.isLoggedIn && user.nickname && user.email && (
          <>
            <form onSubmit={formik.handleSubmit}>
              <Box>
                <Typography variant="h6">
                  Welcome back, <b>{user.nickname}</b>!
                </Typography>
                <Typography
                  fontSize={12}
                  sx={{
                    opacity: 0.75,
                  }}
                  paragraph
                >
                  Logged in as {user.email}
                </Typography>
                <Typography paragraph>
                  Press 'Claim' and we'll send your item straight to your Uniqly
                  account.
                </Typography>
                <Typography paragraph>
                  Want to move it to your personal wallet later? Go right ahead,
                  anytime!
                </Typography>
                <Button
                  type="submit"
                  isLoading={isMintModalOpen}
                  disabled={disabled || isMintModalOpen || !canMint}
                  sx={{ minHeight: 44 }}
                >
                  {buttonText || "CLAIM NFT"}
                </Button>
              </Box>
            </form>
          </>
        )}
        {/* If user is missing either of the required variables, display the standard form. Prefill what's known. */}

        {(!user.nickname || !user.email) && (
          <Box>
            <Typography variant="h6" mb={2}>
              Claim your NFT!
            </Typography>
            <Typography paragraph>
              When you claim your NFT, it's safely stored in a wallet we set up
              for you. Feel free to access or move it whenever you choose.
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <CustomOutlinedInput
                label={nicknameLabel || "Nickname*"}
                inputProps={{
                  id: "nickname",
                  placeholder: `${nicknameLabel || "Nickname"}*`,
                  value: formik.values.nickname,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.nickname}
              />
              <CustomOutlinedInput
                label="E-mail address*"
                inputProps={{
                  id: "email",
                  placeholder: "E-mail address",
                  value: formik.values.email,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.email}
              />
              <CustomOutlinedInput
                label="Confirm E-mail address*"
                inputProps={{
                  id: "confirmEmail",
                  placeholder: "Confirm E-mail address",
                  value: formik.values.confirmEmail,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.confirmEmail}
              />
              {!canMint && (
                <InfoBox type={INFO_BOX_TYPE.INFO}>
                  Mint is only available after scanning the NFC pin from the
                  physical item
                </InfoBox>
              )}
              <Button
                type="submit"
                isLoading={isMintModalOpen}
                disabled={disabled || isMintModalOpen || !canMint}
                sx={{ minHeight: 44 }}
              >
                {buttonText || "CLAIM NFT"}
              </Button>
            </form>
          </Box>
        )}
      </FormikProvider>
      {isMintModalOpen && (
        <MintItemModal
          data={data}
          isPinCodeRequired={isPinCodeRequired}
          isOpen={isMintModalOpen}
          additionalData={formik.values}
          onClose={handleMintModalClose}
          onAfterMint={handleMinted}
          isCards={isCards}
          mintType={NFC_MINT_TYPES.OFFCHAIN}
        />
      )}
      {isAfterMintModalOpen && (
        <AfterMintItemModal
          isOpen={isAfterMintModalOpen}
          itemName={data.itemName}
        />
      )}
    </Box>
  );
};

export default MintItemForm;
